import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useDocumentTitle } from '../../common/useDocumentTitle'

import { selectShouldPollDocument, selectShouldPollDocumentSummary } from '../../store/processDocument/processDocument'
import { startDocumentSummaryPolling, stopDocumentSummaryPolling } from '../../store/processDocument/documentSummary'
import { selectDocument, startDocumentPolling, stopDocumentPolling } from '../../store/processDocument/document'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'

import { DocumentInitialization } from './Initialization/DocumentInitialization'
import { DocumentPoller } from './Poller/DocumentPoller'
import { DocumentLeaveGuardProvider } from './DocumentLeaveConfirmationModal'
import { useDocumentLeaveBypass } from './DocumentLeaveBypassContext'
import { Document } from './Document/Document'

export const DocumentPage = () => {
  useDocumentTitle('Document')

  const { documentId = null } = useParams()

  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  if (!documentId) {
    return null
  }

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  if (!documentSummaryEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['processDocument']} />
  }

  return <DocumentPageContent documentId={documentId} />
}

interface DocumentPageContentProps {
  documentId: string
}

const DocumentPageContent: React.FC<DocumentPageContentProps> = ({ documentId }) => {
  const dispatch = useAppDispatch()

  const shouldPollDocument = useAppSelector(selectShouldPollDocument)
  const shouldPollDocumentSummary = useAppSelector(selectShouldPollDocumentSummary)

  useEffect(() => {
    if (shouldPollDocument) {
      dispatch(startDocumentPolling())
    } else {
      dispatch(stopDocumentPolling())
    }
  }, [dispatch, shouldPollDocument])

  useEffect(() => {
    if (shouldPollDocumentSummary) {
      dispatch(startDocumentSummaryPolling())
    } else {
      dispatch(stopDocumentSummaryPolling())
    }
  }, [dispatch, shouldPollDocumentSummary])

  return (
    <DocumentLeaveGuardProvider>
      <div className='full-size-content'>
        <DocumentRouteCanonicalizer documentId={documentId} />
        <DocumentInitialization documentId={documentId} />
        <DocumentPoller documentId={documentId} />
        <Document />
      </div>
    </DocumentLeaveGuardProvider>
  )
}

interface DocumentRouteCanonicalizerProps {
  documentId: string
}

const DocumentRouteCanonicalizer: React.FC<DocumentRouteCanonicalizerProps> = ({ documentId }) => {
  const navigate = useNavigate()

  const { documentBatchId: urlBatchId = null } = useParams()
  const document = useAppSelector(selectDocument)

  const { bypassOnce } = useDocumentLeaveBypass()

  useEffect(() => {
    if (!document || document.id !== documentId) {
      return
    }

    const realBatchId = document.documentBatchId
    const target = realBatchId ? `/document-batches/${realBatchId}/documents/${documentId}` : `/documents/${documentId}`
    const current = urlBatchId ? `/document-batches/${urlBatchId}/documents/${documentId}` : `/documents/${documentId}`

    if (target !== current) {
      bypassOnce()
      navigate(target, { replace: true })
    }
  }, [document, documentId, urlBatchId, navigate, bypassOnce])

  return null
}
